import { useHistory } from '@fs/zion-router'
import { trackEvent, trackLink } from '@fs/zion-analytics'
import { useFeatureFlag } from '@fs/zion-flags'
import { KIOSK_MENU_PATH, undockKiosk } from '../../helpers/kioskConfig'
import { closeActivity } from '../../hooks/useActivityReturnTo'
import { useActivityExit } from '../ActivityExitProvider'
import { useExperienceNav } from './ExperienceNavContext'
import useIsStationMode from '../../hooks/useIsStationMode'
import { ACTIVITY_CENTER_BASE_PATH } from '../../../activitycenter/constants'
import { FLAGS } from '../../../../config/feature/flags'

const DEFAULT_ANALYTICS_PREFIX = 'ExperienceNavControls'

// A row leads somewhere: a callback to run, else a route to push. Shared by
// every destination the controls offer, so they all report and navigate alike.
const followDestination = (history, destination) => {
  if (destination?.analyticsEventName) {
    trackEvent({ event_name: destination.analyticsEventName })
  }
  if (destination?.onClick) {
    destination.onClick()
    return
  }
  history.push(destination?.to)
}

/**
 * What each control does when it is used. Separated from the component so the
 * rendering reads as a choice between two surfaces rather than a choice
 * between two surfaces plus seven closures.
 *
 * @param {object} options
 * @param {object|null} options.registration a mounted ExperienceNavBack, if any
 * @param {object|null} options.navParent the app-declared level above, if valid
 * @param {string|null} options.kidsReturnTo where the kids app wants us back
 * @param {string|null} options.analyticsPrefix names this experience in close events
 */
export default function useExperienceNavActions({ registration, navParent, kidsReturnTo, analyticsPrefix }) {
  const history = useHistory()
  const isStation = useIsStationMode()
  const activityExit = useActivityExit()
  const nav = useExperienceNav()
  const { isOn: showNewExperiencesLanding } = useFeatureFlag(FLAGS.NEW_EXPERIENCES)

  const leaveKiosk = () => {
    trackEvent({ event_name: `${DEFAULT_ANALYTICS_PREFIX}:LEAVE_KIOSK:CLICK` })
    undockKiosk()
  }

  // Navigation only; reportedBack wraps it with the analytics event.
  const back = () => {
    if (registration?.onClick) {
      registration.onClick()
      return
    }
    history.push(registration?.to)
  }

  const reportedBack = () => {
    trackEvent({ event_name: registration?.analyticsEventName ?? `${DEFAULT_ANALYTICS_PREFIX}:BACK:CLICK` })
    back()
  }

  // Without an app-declared parent the top row IS the registration wearing its
  // own name, so it keeps the registration's event and handler.
  const selectParent = () => (navParent ? followDestination(history, navParent) : reportedBack())

  const selectNavItem = (item) => followDestination(history, item)

  const goHome = () => {
    trackEvent({ event_name: `${DEFAULT_ANALYTICS_PREFIX}:HOME:CLICK` })
    if (isStation) {
      // Only multi-app kiosks ever render Home in STATION (see showHome).
      history.push(KIOSK_MENU_PATH)
      return
    }
    history.push(showNewExperiencesLanding ? ACTIVITY_CENTER_BASE_PATH : '/')
  }

  // Leaves via closeActivity (history pop when the whole visit happened in
  // this document, else a full-page navigation - discovery-kids is a separate
  // SPA this app's router can't render), with ActivityExitProvider's
  // slide-down animation when it's mounted.
  const kidsReturn = () => {
    // Must run first: closeActivity may navigate the document away.
    nav?.runBeforeClose?.()
    // Analytics parity: dashboards segment activity closes by the
    // `<ExperiencePrefix>:CLOSE_ACTIVITY:CLICK` link name. Each app threads its
    // own prefix; the shared name is only the fallback for experiences that
    // never had a dedicated close control.
    trackLink({ name: `${analyticsPrefix ?? DEFAULT_ANALYTICS_PREFIX}:CLOSE_ACTIVITY:CLICK` })
    if (activityExit) {
      activityExit.startExit(() => closeActivity(kidsReturnTo, activityExit.getExitDepth()))
      return
    }
    closeActivity(kidsReturnTo)
  }

  return { leaveKiosk, reportedBack, selectParent, selectNavItem, goHome, kidsReturn }
}
